<template>
  <main class="characters-directory">
    <section class="panel">
      <header class="header">
        <div class="game-picker">
          <button type="button" class="game-title" :aria-expanded="gameMenuOpen" @click="toggleGameMenu">
            <span>{{ selectedGameLabel }}</span>
            <i aria-hidden="true"></i>
          </button>
          <div v-if="gameMenuOpen" class="game-menu" role="menu">
            <button v-for="game in gameTabs" :key="game.key" type="button" :class="{ active: gameFilter === game.key }" @click="selectGame(game.key)">
              {{ game.label }}
            </button>
            <small v-if="!gameTabs.length">No game catalogs available</small>
          </div>
        </div>
        <div class="header-tools">
          <small>{{ filteredCharacters.length }}/{{ characters.filter(item => item.plugin_id === gameFilter).length }}</small>
          <div class="filter-picker">
            <IconAction name="filter" :label="`Filter: ${ownershipFilterLabel}`" :active="ownershipFilter !== 'all'" :size="30" @click="toggleFilterMenu" />
            <div v-if="filterMenuOpen" class="filter-menu" role="menu">
              <button v-for="tab in ownershipTabs" :key="tab.key" type="button" :class="{ active: ownershipFilter === tab.key }" @click="selectOwnershipFilter(tab.key)">
                {{ tab.label }}
              </button>
            </div>
          </div>
          <div class="sort-picker">
            <IconAction name="sort" :label="`Sort: ${sortModeLabel}`" :active="sortMode !== 'activity'" :size="30" @click="toggleSortMenu" />
            <div v-if="sortMenuOpen" class="filter-menu sort-menu" role="menu">
              <button v-for="tab in sortTabs" :key="tab.key" type="button" :class="{ active: sortMode === tab.key }" @click="selectSort(tab.key)">
                {{ tab.label }}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div class="body">
        <div v-if="loading" class="directory-state">Loading characters…</div>
        <div v-else-if="error" class="directory-state error">
          <strong>Could not load the character catalog</strong>
          <span>{{ error }}</span>
          <button type="button" @click="loadCatalog">Retry</button>
        </div>

        <ul v-else class="character-grid">
          <li
            v-for="item in filteredCharacters"
            :key="itemKey(item)"
            class="character-card"
            :class="{ owned: item.owned, playing: isPersonaPlaying(item) }"
            tabindex="0"
            @click="openDetails(item)"
            @keydown.enter.prevent="openDetails(item)"
          >
            <div class="character-media" aria-hidden="true">
              <img :src="artUrl(item)" :alt="item.character.name" :style="characterImageStyle(item.character)" loading="lazy">
            </div>

            <div class="character-main">
              <div class="character-head">
                <div>
                  <small>{{ item.collection?.name || item.plugin_name }}</small>
                  <strong>{{ item.character.name }}</strong>
                </div>
                <div class="character-flags">
                  <span v-if="isAbilityModified(item)" class="ability-modified" title="Owner ability program active">Patch</span>
                  <span class="ownership" :class="{ owned: item.owned }">{{ item.owned ? 'Owned' : acquisitionLabel(item) }}</span>
                </div>
              </div>

              <p>{{ item.character.description || 'Game character' }}</p>

              <div class="performance-row" title="Confirmed positions attributed to this character">
                <span><small>Positions</small><b>{{ performancePositions(item) }}</b></span>
                <span><small>PnL</small><b :class="performancePnlClass(item)">{{ performancePnl(item) }}</b></span>
                <span><small>Win rate</small><b>{{ performanceWinRate(item) }}</b></span>
              </div>

              <div class="account-line" :class="{ missing: !walletFor(item) }">
                <strong :class="{ active: isActivePersona(item) }">{{ walletFor(item) ? walletName(item) : 'Wallet not linked' }}</strong>
                <span>{{ walletFor(item) ? walletBalance(item) : 'No account assigned' }}</span>
                <em :class="walletFor(item) ? walletPnlClass(item) : 'neutral'">{{ walletFor(item) ? walletPnl(item) : '—' }}</em>
              </div>

              <footer class="card-footer">
                <div class="tags">
                  <span v-if="item.character.rarity">{{ item.character.rarity }}</span>
                  <span v-for="trait in (item.character.traits || []).slice(0, 2)" :key="trait.id">{{ trait.value }}</span>
                </div>
                <strong class="nft-price">{{ priceLabel(item) }}</strong>
                <IconAction :name="actionIcon(item)" :label="actionLabel(item)" :size="28" :disabled="busyKey === itemKey(item)" @click.stop="primaryAction(item)" />
              </footer>
            </div>
          </li>
          <li v-if="filteredCharacters.length === 0" class="empty"><strong>{{ emptyMessage }}</strong></li>
        </ul>
      </div>
    </section>

    <div v-if="selected" class="detail-overlay" @click.self="closeDetails">
      <aside class="detail-card" role="dialog" aria-modal="true" :aria-label="selected.character.name">
        <button class="detail-close" type="button" aria-label="Close" @click="closeDetails">×</button>

        <div class="detail-hero">
          <span class="detail-frame" :style="detailFrameStyle(selected.character)">
            <img :src="artUrl(selected)" :alt="selected.character.name" :style="detailImageStyle(selected.character)">
          </span>
        </div>

        <section class="detail-copy">
          <small>{{ selected.collection?.name || selected.plugin_name }}</small>
          <h2>{{ selected.character.name }}</h2>
          <div class="detail-badges">
            <span v-if="selected.character.rarity">{{ selected.character.rarity }}</span>
            <span v-if="selected.character.edition">{{ selected.character.edition }}</span>
            <span v-for="tag in (selected.character.tags || []).slice(0, 3)" :key="tag">{{ tag }}</span>
          </div>
          <p>{{ selected.character.description || 'Game character' }}</p>
          <div class="detail-commerce">
            <strong class="detail-price">{{ priceLabel(selected) }}</strong>
            <div class="detail-quick-actions">
              <IconAction name="info" label="Info" :active="detailTab === 'info'" @click="selectDetailTab('info')" />
              <IconAction v-if="selected.owned" name="star" label="Game account" :active="detailTab === 'account'" @click="selectDetailTab('account')" />
              <IconAction v-if="selected.owned && walletFor(selected)" name="activity" label="Positions" :active="detailTab === 'trades'" @click="openWalletTrades(selected)" />
              <IconAction v-if="selected.owned && walletFor(selected)" name="send" label="Transfer" :active="detailTab === 'transfer'" @click="openTransfer(selected)" />
              <IconAction
                :name="actionIcon(selected)"
                :label="actionLabel(selected)"
                :active="isPersonaPlaying(selected)"
                :disabled="busyKey === itemKey(selected)"
                @click="primaryAction(selected)"
              />
            </div>
          </div>
        </section>

        <div class="detail-pane" :class="`tab-${detailTab}`">
        <section v-if="detailTab === 'info'" class="collection-copy">
          <small>COLLECTION</small>
          <strong>{{ selected.collection.name || selected.plugin_name }}</strong>
          <p>{{ selected.collection?.description || 'Heroes of this game.' }}</p>
        </section>

        <section v-if="detailTab === 'info'" class="detail-traits">
          <div v-for="trait in visibleDetailTraits(selected.character)" :key="trait.id">
            <small>{{ trait.name }}</small>
            <strong>{{ trait.value }}</strong>
          </div>
        </section>

        <section v-if="detailTab === 'info'" class="detail-abilities">
          <div v-for="skill in selected.character.skills || []" :key="skill.id">
            <span><strong>{{ skill.name }}</strong><b>{{ abilityRating(skill) }}</b></span>
            <p>{{ skill.description }}</p>
            <small>{{ abilityStyle(skill) }} · {{ abilityEvidence(skill) }}</small>
          </div>
        </section>

        <section v-if="detailTab === 'info'" class="ability-details" :class="{ modified: isAbilityModified(selected) }">
          <header>
            <div><small>HOW IT TRADES</small><strong>Real-market behavior</strong></div>
            <button v-if="abilityAuthoring" class="ability-wand inline" type="button" aria-label="Patch with AI" title="Patch with AI" @click="showAbilityHelp = true">🪄</button>
          </header>
          <div v-for="skill in selected.character.skills || []" :key="`details:${skill.id}`">
            <header>
              <span><strong>{{ skill.name }}</strong><small>{{ skill.stats?.program_name || abilityStyle(skill) }}</small></span>
              <IconAction v-if="skill.stats?.resettable" name="reset" label="Reset this ability patch" tone="danger" :size="28" :disabled="abilityBusy" @click="resetAbility(skill)" />
            </header>
            <p>{{ skill.stats?.technical_description || 'Technical behavior is not available.' }}</p>
            <div v-if="skill.stats?.modified" class="patch-copy">
              <small>PREMIUM PATCH</small>
              <p>{{ skill.stats?.patch_description || 'Custom owner program. No patch description was recorded for this legacy version.' }}</p>
            </div>
          </div>
          <p v-if="abilityError" class="settings-error">{{ abilityError }}</p>
        </section>

        <section v-if="detailTab === 'account' && selected.owned" class="detail-account">
          <button
            type="button"
            class="account-summary"
            :class="{ open: walletPickerOpen }"
            :aria-expanded="walletPickerOpen"
            :disabled="busyKey === itemKey(selected)"
            @click="walletPickerOpen = !walletPickerOpen"
          >
            <div>
              <small>GAME ACCOUNT</small>
              <strong>{{ walletFor(selected) ? walletName(selected) : 'Select wallet' }}</strong>
              <span>{{ walletFor(selected) ? shortKey(selected.wallet_public_key) : 'No wallet assigned' }}</span>
            </div>
            <div v-if="walletFor(selected)" class="account-money">
              <strong>{{ walletBalance(selected) }}</strong>
              <span :class="walletPnlClass(selected)">{{ walletPnl(selected) }}</span>
            </div>
            <span class="account-chevron" aria-hidden="true">&#8250;</span>
          </button>

          <div v-if="walletPickerOpen" class="wallet-assignment" role="listbox" aria-label="Game account">
            <button
              v-for="wallet in assignmentWallets"
              :key="wallet.public_key"
              type="button"
              :class="{ selected: wallet.public_key === selected.wallet_public_key }"
              :disabled="busyKey === itemKey(selected)"
              @click="toggleWalletBinding(wallet)"
            >
              <span class="wallet-choice-mark" :class="{ selected: wallet.public_key === selected.wallet_public_key }" aria-hidden="true"></span>
              <span>
                <strong>{{ wallet.name || 'Game account' }}</strong>
                <small>{{ shortKey(wallet.public_key) }}</small>
              </span>
              <em>{{ formatLamports(wallet.lamports || 0) }}</em>
            </button>
            <p v-if="!assignmentWallets.length" class="binding-empty">No free wallets</p>
            <p v-if="bindingError" class="settings-error">{{ bindingError }}</p>
          </div>

          <div v-if="settingsOpen && walletFor(selected)" class="account-settings">
            <label class="name-field"><span>Name</span><input v-model="settingsDraft.name" maxlength="64" type="text"></label>
            <label class="toggle-field">
              <span>Autosnipe</span>
              <button type="button" class="toggle" :class="{ on: settingsDraft.enabled }" @click="settingsDraft.enabled = !settingsDraft.enabled">
                {{ settingsDraft.enabled ? 'ON' : 'OFF' }}
              </button>
            </label>
            <label><span>Deposit %</span><NumberStepper v-model="settingsDraft.pct" :min="1" :max="100" :step="1" /></label>
            <label><span>CUL</span><NumberStepper v-model="settingsDraft.cul" :min="0" :step="100000" /></label>
            <label><span>CUP</span><NumberStepper v-model="settingsDraft.cup" :min="0" :step="10000" /></label>
            <label><span>TP %</span><NumberStepper v-model="settingsDraft.takeProfitPct" :min="0" :step="0.1" /></label>
            <label><span>SL %</span><NumberStepper v-model="settingsDraft.stopLossPct" :min="0" :step="0.1" /></label>
            <label><span>TTL sec</span><NumberStepper v-model="settingsDraft.ttlSec" :min="0" :step="1" /></label>
            <p v-if="settingsError" class="settings-error">{{ settingsError }}</p>
            <div class="settings-actions">
              <button type="button" @click="showPrivate">Private key</button>
              <button type="button" class="save" :disabled="settingsBusy" @click="saveSettings">Save</button>
            </div>
          </div>
        </section>

        <section v-if="detailTab === 'trades' && selected.owned && walletFor(selected)" class="detail-trades"><WalletTrades embedded :wallet="String(selected.wallet_public_key || '')" @close="selectDetailTab('info')" /></section>

        <section v-if="detailTab === 'transfer' && selected.owned && walletFor(selected)" class="detail-transfer">
          <Transfer
            embedded
            :visible="true"
            source-kind="wallet"
            :source="transferWallet"
            :user="store.user || {}"
            :wallets="wallets"
            :busy="transferBusy"
            :error="transferError"
            @submit="submitTransfer"
            @close="selectDetailTab('info')"
          />
        </section>
        </div>
      </aside>
    </div>

    <div v-if="showAbilityHelp" class="acquisition-overlay" @click.self="showAbilityHelp = false">
      <section class="acquisition-card ability-help" role="dialog" aria-modal="true" aria-label="Patch character with AI">
        <header><div><small>PREMIUM</small><h2>Patch with AI</h2></div><button type="button" aria-label="Close" @click="showAbilityHelp = false">×</button></header>
        <p>Ask your connected AiMe agent to change this owned character’s entry or position behavior. The agent must explain the patch, validate and dry-run it, activate an immutable program, then bind it to this character.</p>
        <div class="ability-prompt"><small>TRY</small><strong>“Make {{ selected?.character?.name }} enter more selectively and explain what changed.”</strong></div>
        <p>The game never receives trading authority. Wallet ownership, premium access and platform trade guards remain authoritative.</p>
      </section>
    </div>

    <div v-if="acquisitionItem" class="acquisition-overlay" @click.self="closeAcquisition()">
      <section class="acquisition-card" role="dialog" aria-modal="true" aria-labelledby="acquisition-title">
        <header>
          <div>
            <small>{{ acquisitionItem.collection?.name || acquisitionItem.plugin_name }}</small>
            <h2 id="acquisition-title">{{ acquisitionTitle }}</h2>
          </div>
          <button type="button" aria-label="Close" :disabled="!!busyKey" @click="closeAcquisition()">×</button>
        </header>
        <p class="acquisition-copy">
          {{ acquisitionItem.character.name }} will become the static character for exactly one game account.
          This selection is confirmed before any claim or payment.
        </p>

        <fieldset v-if="availableWallets.length" class="wallet-choice">
          <legend>Choose a free game account</legend>
          <label v-for="wallet in availableWallets" :key="wallet.public_key" :class="{ selected: acquisitionWallet === wallet.public_key }">
            <input v-model="acquisitionWallet" type="radio" :value="wallet.public_key">
            <span><strong>{{ wallet.name || 'Game account' }}</strong><small>{{ shortKey(wallet.public_key) }}</small></span>
            <em>{{ formatLamports(wallet.lamports || 0) }}</em>
          </label>
        </fieldset>

        <section v-else class="new-wallet-choice">
          <strong>No free game accounts</strong>
          <p>Create a new managed account, then {{ acquisitionVerb.toLowerCase() }} and bind this character to it.</p>
          <label><span>Account name</span><input v-model="newWalletName" maxlength="64" type="text" placeholder="Citadel account"></label>
        </section>

        <div class="acquisition-summary">
          <span>{{ acquisitionVerb }}</span>
          <strong>{{ priceLabel(acquisitionItem) }}</strong>
          <small>{{ availableWallets.length ? shortKey(acquisitionWallet) : 'New managed account' }}</small>
        </div>
        <p v-if="acquisitionError" class="acquisition-error">{{ acquisitionError }}</p>
        <footer>
          <button type="button" :disabled="!!busyKey" @click="closeAcquisition()">Cancel</button>
          <button class="confirm" type="button" :disabled="!!busyKey || (availableWallets.length > 0 && !acquisitionWallet)" @click="confirmAcquisition">
            {{ busyKey ? 'Working…' : `Confirm ${acquisitionVerb.toLowerCase()}` }}
          </button>
        </footer>
      </section>
    </div>

    <ImportModal
      :visible="privateOpen"
      :public-key="privatePk"
      :private-key="privateKey"
      :error="privateError"
      @close="closePrivate"
    />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, unref, watch } from 'vue'
import { useStorage } from '@/stores'
import { makePluginsApi } from '@/stores/api/plugins'
import { makeWalletsApi } from '@/stores/api/wallets'
import Transfer from '@/components/wallet/transfer.vue'
import ImportModal from '@/components/wallet/import.vue'
import NumberStepper from '@/components/_shared/NumberStepper.vue'
import WalletTrades from '@/components/wallet/trades.vue'
import IconAction from '@/components/_shared/IconAction.vue'
import { walletRows } from '@/shell/pnl'
import { resolveCharacterLink } from './characterLink'
import { formatLamports } from '@/composables/format'
import { formatPnlWithPct, shortKey } from '@/components/_shared/format'

const store = useStorage()
const api = makePluginsApi()
const walletsApi = makeWalletsApi()

const props = defineProps({
  pluginId: { type: String, default: '' },
  characterId: { type: String, default: '' },
  wallet: { type: String, default: '' },
})

const loading = ref(true)
const error = ref('')
const characters = ref([])
const selected = ref(null)
const walletPickerOpen = ref(false)
const bindingError = ref('')
const ownershipFilter = ref('all')
const gameFilter = ref('')
const gameSelectionTouched = ref(false)
const gameMenuOpen = ref(false)
const filterMenuOpen = ref(false)
const sortMenuOpen = ref(false)
const sortMode = computed(() => {
  const mode = String(store.settings?.nft?.sort?.mode || 'activity')
  return sortTabs.some(tab => tab.key === mode) ? mode : 'activity'
})
const detailTab = ref('info')
const pendingExternalCharacter = ref(null)
const busyKey = ref('')
const transferItem = ref(null)
const transferBusy = ref(false)
const transferError = ref('')
const settingsOpen = ref(false)
const settingsBusy = ref(false)
const settingsError = ref('')
const abilityBusy = ref(false)
const abilityError = ref('')
const showAbilityHelp = ref(false)
const privateOpen = ref(false)
const privatePk = ref('')
const privateKey = ref('')
const privateError = ref('')
const acquisitionItem = ref(null)
const acquisitionWallet = ref('')
const acquisitionError = ref('')
const newWalletName = ref('')
const settingsDraft = reactive({
  name: '', pct: 25, cup: 0, cul: 0,
  takeProfitPct: 0, stopLossPct: 0, ttlSec: 0, enabled: true,
})

const ownershipTabs = [
  { key: 'all', label: 'All' },
  { key: 'owned', label: 'Owned' },
  { key: 'available', label: 'Available' },
]
const sortTabs = [
  { key: 'activity', label: 'Activity' },
  { key: 'pnl', label: 'PnL' },
  { key: 'wr', label: 'WR' },
]

const state = computed(() => unref(store.state) || {})
const selectedWallet = computed(() => unref(store.selectedWallet) || null)
const wallets = computed(() => walletRows(store.wallets || {}))
const abilityAuthoring = computed(() => {
  const role = String(store.user?.role || state.value?.user?.role || 'user').toLowerCase()
  return role === 'admin' || role === 'developer' || wallets.value.some(wallet => wallet?.premium === true)
})
const transferWallet = computed(() => transferItem.value ? walletFor(transferItem.value) : null)
const tradeOwners = computed(() => {
  const value = state.value?.settings?.trade?.owners
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
})
const autotradeSettings = computed(() => {
  const value = state.value?.settings?.autotrade || store.autotrade
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
})
const gameTabs = computed(() => {
  const rows = new Map()
  for (const item of characters.value) rows.set(item.plugin_id, item.plugin_name)
  const active = activeGameID()
  return [...rows].map(([key, label]) => ({ key, label }))
    .sort((a, b) => Number(b.key === active) - Number(a.key === active) || String(a.label).localeCompare(String(b.label)))
})
const selectedGameLabel = computed(() => gameTabs.value.find(game => game.key === gameFilter.value)?.label || 'Select…')
const ownershipFilterLabel = computed(() => ownershipTabs.find(tab => tab.key === ownershipFilter.value)?.label || 'All')
const sortModeLabel = computed(() => sortTabs.find(tab => tab.key === sortMode.value)?.label || 'Activity')
const emptyMessage = computed(() => gameFilter.value ? 'No characters match this filter' : 'Select a game to browse its character catalog')
const filteredCharacters = computed(() => characters.value
  .filter(item => {
    if (!gameFilter.value || item.plugin_id !== gameFilter.value) return false
    if (ownershipFilter.value === 'owned' && !item.owned) return false
    if (ownershipFilter.value === 'available' && item.owned) return false
    return true
  })
  .sort((a, b) => Number(ownershipFilter.value === 'all' && b.owned) - Number(ownershipFilter.value === 'all' && a.owned)
    || characterSortValue(b, sortMode.value) - characterSortValue(a, sortMode.value)
    || String(a.character?.name || '').localeCompare(String(b.character?.name || ''))))
const availableWallets = computed(() => {
  const item = acquisitionItem.value
  if (!item) return []
  const occupied = new Set(characters.value
    .filter(row => row.plugin_id === item.plugin_id && String(row.wallet_public_key || '').trim())
    .map(row => String(row.wallet_public_key).trim()))
  return wallets.value.filter(wallet => !occupied.has(wallet.public_key))
})
const assignmentWallets = computed(() => {
  const item = selected.value
  if (!item?.owned) return []
  const current = String(item.wallet_public_key || '').trim()
  const occupied = new Set(characters.value
    .filter(row => row.plugin_id === item.plugin_id
      && characterKey(row) !== characterKey(item)
      && String(row.wallet_public_key || '').trim())
    .map(row => String(row.wallet_public_key).trim()))
  return wallets.value.filter(wallet => wallet.public_key === current || !occupied.has(wallet.public_key))
})
const acquisitionVerb = computed(() => {
  if (acquisitionItem.value?.owned) return 'Bind'
  return acquisitionItem.value?.acquisition === 'free' ? 'Claim' : 'Buy'
})
const acquisitionTitle = computed(() => availableWallets.value.length
  ? `${acquisitionVerb.value} for a game account`
  : 'Create a game account')

function activeGameID() {
  const installations = Array.isArray(state.value?.uiPluginInstallations) ? state.value.uiPluginInstallations : []
  const active = installations.find(item => item?.active && item?.enabled !== false)
  return String(active?.plugin_id || active?.plugin?.id || '').trim()
}
function applyGameFilter(pluginId) {
  const value = String(pluginId || '').trim()
  if (value) gameFilter.value = value
  gameMenuOpen.value = false
}
function selectGame(pluginId) {
  gameSelectionTouched.value = true
  gameFilter.value = String(pluginId || '').trim()
  gameMenuOpen.value = false
  const url = new URL(window.location.href)
  if (gameFilter.value) url.searchParams.set('plugin_id', gameFilter.value)
  else url.searchParams.delete('plugin_id')
  url.searchParams.delete('character_id')
  url.searchParams.delete('wallet')
  url.searchParams.set('workspace', 'nft')
  window.history.replaceState({}, '', url)
}
function toggleGameMenu() {
  gameMenuOpen.value = !gameMenuOpen.value
  filterMenuOpen.value = false
  sortMenuOpen.value = false
}
function toggleFilterMenu() {
  filterMenuOpen.value = !filterMenuOpen.value
  gameMenuOpen.value = false
  sortMenuOpen.value = false
}
function toggleSortMenu() {
  sortMenuOpen.value = !sortMenuOpen.value
  gameMenuOpen.value = false
  filterMenuOpen.value = false
}
function selectOwnershipFilter(key) {
  ownershipFilter.value = ownershipTabs.some(tab => tab.key === key) ? key : 'all'
  filterMenuOpen.value = false
}
function selectSort(key) {
  const mode = sortTabs.some(tab => tab.key === key) ? key : 'activity'
  sortMenuOpen.value = false
  void store.updateNft({ sort: { mode, dir: -1 } })
}
function closeMenus(event) {
  if (event?.target?.closest?.('.game-picker, .filter-picker, .sort-picker')) return
  gameMenuOpen.value = false
  filterMenuOpen.value = false
  sortMenuOpen.value = false
}

function onCatalogFilter(event) { applyGameFilter(event?.detail?.pluginId) }
function onExternalCharacter(event) {
  const d = event?.detail && typeof event.detail === 'object' ? event.detail : {}
  pendingExternalCharacter.value = { pluginId: String(d.pluginId || '').trim(), characterId: String(d.characterId || '').trim(), wallet: String(d.wallet || '').trim() }
  applyGameFilter(pendingExternalCharacter.value.pluginId)
  if (characters.value.length) revealExternalCharacter()
  else void loadCatalog()
}
function revealExternalCharacter() {
  const t = pendingExternalCharacter.value
  if (!t?.pluginId || !t?.characterId || !characters.value.length) return
  const item = resolveCharacterLink(characters.value, t)
  if (!item) return
  gameFilter.value = item.plugin_id
  ownershipFilter.value = 'all'
  selected.value = item
  detailTab.value = 'info'
  pendingExternalCharacter.value = null
}

watch(
  () => [props.pluginId, props.characterId, props.wallet],
  ([pluginId, characterId, wallet]) => {
    applyGameFilter(pluginId)
    const targetPlugin = String(pluginId || '').trim()
    const targetCharacter = String(characterId || '').trim()
    if (!targetPlugin || !targetCharacter) {
      pendingExternalCharacter.value = null
      selected.value = null
      return
    }
    pendingExternalCharacter.value = {
      pluginId: targetPlugin,
      characterId: targetCharacter,
      wallet: String(wallet || '').trim(),
    }
    if (characters.value.length) revealExternalCharacter()
    else if (!loading.value) void loadCatalog()
  },
  { immediate: true },
)

watch(
  () => activeGameID(),
  active => {
    if (gameSelectionTouched.value || pendingExternalCharacter.value || !active) return
    if (characters.value.some(item => item.plugin_id === active)) gameFilter.value = active
  },
)

onMounted(() => {
  const query = new URLSearchParams(window.location.search)
  const pluginId = String(query.get('plugin_id') || '').trim()
  const characterId = String(query.get('character_id') || '').trim()
  applyGameFilter(pluginId)
  if (pluginId && characterId) {
    pendingExternalCharacter.value = {
      pluginId,
      characterId,
      wallet: String(query.get('wallet') || '').trim(),
    }
  }
  window.addEventListener('aime-nft-filter', onCatalogFilter)
  window.addEventListener('aime-nft-character', onExternalCharacter)
  document.addEventListener('click', closeMenus)
  void loadCatalog()
})

onBeforeUnmount(() => {
  window.removeEventListener('aime-nft-filter', onCatalogFilter)
  window.removeEventListener('aime-nft-character', onExternalCharacter)
  document.removeEventListener('click', closeMenus)
})

async function loadCatalog() {
  loading.value = true
  error.value = ''
  try {
    const result = await api.listCharacterCatalog()
    characters.value = normalizeCatalogItems(Array.isArray(result?.items) ? result.items : [])
    const availableGames = new Set(characters.value.map(item => item.plugin_id))
    if (!pendingExternalCharacter.value && !gameSelectionTouched.value) {
      const requested = String(props.pluginId || '').trim()
      const active = activeGameID()
      gameFilter.value = availableGames.has(requested)
        ? requested
        : availableGames.has(active)
          ? active
          : (characters.value[0]?.plugin_id || '')
    } else if (!pendingExternalCharacter.value && !availableGames.has(gameFilter.value)) {
      gameFilter.value = characters.value[0]?.plugin_id || ''
    }
    if (selected.value) selected.value = characters.value.find(item => itemKey(item) === itemKey(selected.value)) || null
    revealExternalCharacter()
  } catch (cause) {
    error.value = cause?.message || 'character_catalog_failed'
  } finally {
    loading.value = false
  }
}

function characterKey(item) { return [item?.plugin_id, item?.character?.id].join(':') }
function characterSortValue(item, mode) {
  const stats = item?.character?.performance || {}
  if (mode === 'pnl') return Number(stats.pnl_lamports || 0)
  if (mode === 'wr') return Number(stats.win_rate || 0)
  const activity = Date.parse(String(stats.last_activity_at || ''))
  return Number.isFinite(activity) ? activity : 0
}
function ownershipRank(item) {
  return (item?.owned ? 100 : 0) + (String(item?.wallet_public_key || '').trim() ? 20 : 0) + (item?.installation_id ? 5 : 0) + (item?.nft_mint ? 1 : 0)
}
function normalizeCatalogItems(items) {
  const unique = new Map()
  for (const item of items) {
    const key = characterKey(item)
    const current = unique.get(key)
    if (!current || ownershipRank(item) > ownershipRank(current)) unique.set(key, item)
  }
  return [...unique.values()].sort((a, b) =>
    Number(Boolean(b.owned)) - Number(Boolean(a.owned))
    || String(a.plugin_name || '').localeCompare(String(b.plugin_name || ''))
    || String(a.character?.name || '').localeCompare(String(b.character?.name || '')))
}
function itemKey(item) { return characterKey(item) }
function walletFor(item) {
  const key = String(item?.wallet_public_key || '').trim()
  return key ? wallets.value.find(wallet => wallet.public_key === key) || null : null
}
function walletName(item) {
  const wallet = walletFor(item)
  return String(wallet?.name || '').trim() || shortKey(item?.wallet_public_key)
}
function walletBalance(item) { return formatLamports(walletFor(item)?.lamports || 0) }
function walletPnlValue(item) {
  const wallet = walletFor(item) || {}
  const period = 'day'
  if (period === 'day') return [Number(wallet.pnl_day ?? wallet.pnlDay ?? 0), Number(wallet.pnl_day_cost ?? wallet.pnlDayCost ?? 0)]
  if (period === 'week') return [Number(wallet.pnl_week ?? wallet.pnlWeek ?? 0), Number(wallet.pnl_week_cost ?? wallet.pnlWeekCost ?? 0)]
  if (period === 'month') return [Number(wallet.pnl_month ?? wallet.pnlMonth ?? 0), Number(wallet.pnl_month_cost ?? wallet.pnlMonthCost ?? 0)]
  return [Number(wallet.pnl_lamports ?? wallet.pnlLamports ?? 0), Number(wallet.pnl_cost ?? wallet.pnlCost ?? 0)]
}
function walletPnl(item) {
  const [value, basis] = walletPnlValue(item)
  return formatPnlWithPct(value, basis)
}
function walletPnlClass(item) {
  const [value] = walletPnlValue(item)
  return value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral'
}
function installationFor(item) {
  const id = String(item?.installation_id || '').trim()
  const pluginId = String(item?.plugin_id || '').trim()
  const installations = Array.isArray(state.value?.uiPluginInstallations) ? state.value.uiPluginInstallations : []
  return installations.find(row => String(row?.id || '').trim() === id)
    || installations.find(row => String(row?.plugin_id || row?.plugin?.id || '').trim() === pluginId)
    || null
}
function isPluginActive(item) {
  const installation = installationFor(item)
  if (installation?.active === true && installation?.enabled !== false) return true
  try { return String(JSON.parse(window.localStorage?.getItem('aime.workspace.ownerDraft') || 'null')?.pluginId || '') === String(item?.plugin_id || '') } catch { return false }
}
function isActivePersona(item) {
  return String(item?.wallet_public_key || '') === String(selectedWallet.value?.public_key || '')
}
function isPersonaPlaying(item) {
  return Boolean(item?.owned && walletFor(item) && isPluginActive(item) && isActivePersona(item))
}
async function refreshInstallations() {
  const result = await api.listInstallations()
  const items = Array.isArray(result?.items) ? result.items : []
  store.setUIPluginInstallations(items)
  return items
}
function selectPersona(item) {
  const key = String(item?.wallet_public_key || '').trim()
  if (key) store.setSelectedWalletKey(key)
}
function openWalletTrades(item) {
  const key = String(item?.wallet_public_key || '').trim()
  if (!key) return
  selectPersona(item)
  selectDetailTab('trades')
}
function openTransfer(item) {
  if (!walletFor(item)) return
  selectPersona(item)
  transferError.value = ''
  transferItem.value = item
  selectDetailTab('transfer')
}
function closeTransfer() {
  transferItem.value = null
  transferError.value = ''
  if (detailTab.value === 'transfer') selectDetailTab('info')
}
async function submitTransfer(payload) {
  if (transferBusy.value) return
  transferBusy.value = true
  transferError.value = ''
  try {
    await store.transferWallet(payload)
    closeTransfer()
  } catch (cause) {
    transferError.value = cause?.message || 'transfer_failed'
  } finally {
    transferBusy.value = false
  }
}

function normalizePct(value) {
  const pct = Math.floor(Number(value))
  return Number.isFinite(pct) ? Math.min(100, Math.max(1, pct)) : 25
}
function ownerConfig(publicKey) {
  const key = String(publicKey || '').trim()
  const raw = tradeOwners.value[key]
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
  if (raw != null) return { pct: normalizePct(raw), selected: true, enabled: true }
  return { pct: 25, selected: false, enabled: true }
}
function hydrateSettings(item) {
  const wallet = walletFor(item)
  const config = ownerConfig(wallet?.public_key)
  settingsDraft.name = String(wallet?.name || '').trim()
  settingsDraft.pct = normalizePct(config.pct)
  settingsDraft.cup = Math.max(0, Number(config.cup || 0))
  settingsDraft.cul = Math.max(0, Number(config.cul || 0))
  settingsDraft.takeProfitPct = Math.max(0, Number(config.takeProfitBps || 0) / 100)
  settingsDraft.stopLossPct = Math.max(0, Number(config.stopLossBps || 0) / 100)
  settingsDraft.ttlSec = Math.max(0, Math.round(Number(config.maxHoldMs || 0) / 1000))
  const auto = autotradeSettings.value
  const key = String(wallet?.public_key || '').trim()
  settingsDraft.enabled = config.enabled !== false
    && auto.enabled === true
    && String(auto.wallet || '').trim() === key
}
async function saveSettings() {
  const item = selected.value
  const wallet = walletFor(item)
  const key = String(wallet?.public_key || '').trim()
  if (!key || settingsBusy.value) return
  settingsBusy.value = true
  settingsError.value = ''
  try {
    const previous = ownerConfig(key)
    await store.updateTrade({
      owners: {
        ...tradeOwners.value,
        [key]: {
          ...previous,
          pct: normalizePct(settingsDraft.pct),
          cup: Math.max(0, Math.floor(Number(settingsDraft.cup || 0))),
          cul: Math.max(0, Math.floor(Number(settingsDraft.cul || 0))),
          takeProfitBps: Math.max(0, Math.floor(Number(settingsDraft.takeProfitPct || 0) * 100)),
          takeProfitLamports: 0,
          stopLossBps: Math.max(0, Math.floor(Number(settingsDraft.stopLossPct || 0) * 100)),
          maxHoldMs: Math.max(0, Math.floor(Number(settingsDraft.ttlSec || 0) * 1000)),
          enabled: settingsDraft.enabled !== false,
          selected: previous.selected !== false,
        },
      },
    })
    const autoEnabled = settingsDraft.enabled !== false
    const currentAutoWallet = String(autotradeSettings.value?.wallet || '').trim()
    if (autoEnabled) {
      await store.updateAutotrade({ enabled: true, wallet: key })
    } else if (currentAutoWallet === key) {
      await store.updateAutotrade({ enabled: false, wallet: key })
    }
    const name = String(settingsDraft.name || '').trim()
    if (name && name !== String(wallet?.name || '').trim()) await store.renameWallet(key, name)
    settingsOpen.value = false
  } catch (cause) {
    settingsError.value = cause?.message || 'persona_account_save_failed'
  } finally {
    settingsBusy.value = false
  }
}
async function showPrivate() {
  const key = String(walletFor(selected.value)?.public_key || '').trim()
  if (!key) return
  privatePk.value = key
  privateKey.value = ''
  privateError.value = ''
  privateOpen.value = true
  try {
    const result = await store.importWallet(key)
    privatePk.value = result?.public_key || key
    privateKey.value = result?.private_key || ''
  } catch (cause) {
    privateError.value = cause?.message || 'wallet_import_failed'
  }
}
function closePrivate() {
  privateOpen.value = false
  privatePk.value = ''
  privateKey.value = ''
  privateError.value = ''
}

function acquisitionLabel(item) { return item.acquisition === 'free' ? 'Available' : 'Marketplace' }
function purchasePrice(item) {
  const lamports = Number(item?.price_lamports || 0)
  return lamports > 0 ? `${(lamports / 1e9).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} SOL` : ''
}
function priceLabel(item) {
  if (item.owned) return item.nft_mint ? 'NFT' : 'OWNED'
  if (item.acquisition === 'free') return 'FREE'
  return purchasePrice(item) || 'FOR SALE'
}
function visibleDetailTraits(character) {
  const traits = Array.isArray(character?.traits) ? character.traits : []
  return Array.from({ length: 3 }, (_, i) => traits[i] || { id: `unassigned-${i}`, name: ['Role', 'Domain', 'Temperament'][i], value: '—' }).map((trait, i) => ({
    id: String(trait?.id || i),
    name: String(trait?.name || 'Trait'),
    value: String(trait?.value || '—'),
  }))
}
function actionLabel(item) {
  if (item.owned && !walletFor(item)) return 'Bind account'
  if (item.owned) return isPersonaPlaying(item) ? 'Pause' : 'Play'
  if (item.acquisition === 'free') return 'Claim'
  return 'Buy'
}
function actionIcon(item) {
  if (item.owned && !walletFor(item)) return 'link'
  if (item.owned) return isPersonaPlaying(item) ? 'pause' : 'play'
  if (item.acquisition === 'free') return 'gift'
  return 'cart'
}
function isAbilityModified(item) {
  return (item?.character?.skills || []).some(skill => skill?.stats?.modified === true)
}
function abilityRating(skill) {
  const stats = skill?.stats || {}
  return stats.ready ? String(Number(stats.rating || 0)) : '—'
}
function abilityStyle(skill) {
  const styles = { careful: 'Careful', balanced: 'Balanced', bold: 'Bold' }
  return styles[String(skill?.style || 'balanced')] || 'Balanced'
}
function abilityEvidence(skill) {
  const stats = skill?.stats || {}
  const samples = Number(stats.samples || 0)
  if (!stats.ready) return samples ? `${samples} completed` : 'No completed battles'
  return `WR ${Number(stats.win_rate || 0)}% · ${samples} completed`
}
function characterPerformance(item) { return item?.character?.performance || null }
function performancePositions(item) {
  const stats = characterPerformance(item)
  const positions = Number(stats?.positions)
  if (Number.isFinite(positions)) return String(Math.max(0, positions))
  const trades = Number(stats?.trades)
  return Number.isFinite(trades) ? String(Math.max(0, trades)) : '—'
}
function performancePnl(item) {
  const value = Number(characterPerformance(item)?.pnl_lamports)
  return Number.isFinite(value) ? formatLamports(value) : '—'
}
function performancePnlClass(item) {
  const value = Number(characterPerformance(item)?.pnl_lamports)
  return Number.isFinite(value) ? value > 0 ? 'positive' : value < 0 ? 'negative' : '' : ''
}
function performanceWinRate(item) {
  const stats = characterPerformance(item)
  const positions = Number(stats?.positions || 0)
  return stats && positions > 0 ? `${Math.round(Number(stats.win_rate || 0))}%` : '—'
}
function encodedPath(path) { return String(path || '').split('/').map(encodeURIComponent).join('/') }
function artUrl(item) {
  const path = String(item.character?.art?.portrait || item.character?.art?.full || '').replace(/^\/+/, '')
  if (!path) return ''
  return `/api/plugins/characters/media/${encodeURIComponent(item.plugin_id)}/${encodeURIComponent(item.version_id)}/${encodedPath(path)}`
}
function frameStyle(character, targetW, targetH) {
  const frame = character?.art?.frame
  const sheet = character?.art?.sheet
  const fw = Number(frame?.width)
  const fh = Number(frame?.height)
  const sw = Number(sheet?.width)
  const sh = Number(sheet?.height)
  if (!(fw > 0) || !(fh > 0)) {
    return { width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }
  }
  const scale = Math.max(targetW / fw, targetH / fh)
  if (sw > 0 && sh > 0) {
    return {
      width: `${sw * scale}px`,
      height: `${sh * scale}px`,
      maxWidth: 'none',
      left: `${-(Number(frame.x) || 0) * scale}px`,
      top: `${-(Number(frame.y) || 0) * scale}px`,
    }
  }
  return { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }
}
function characterImageStyle(character) { return frameStyle(character, 116, 174) }
function detailFrameStyle(character) {
  const width = Number(character?.art?.frame?.width)
  const height = Number(character?.art?.frame?.height)
  return width > 0 && height > 0 ? { aspectRatio: `${width} / ${height}` } : {}
}
function detailImageStyle(character) {
  const frame = character?.art?.frame
  const sheet = character?.art?.sheet
  const fw = Number(frame?.width)
  const fh = Number(frame?.height)
  const sw = Number(sheet?.width)
  const sh = Number(sheet?.height)
  if (fw > 0 && fh > 0 && sw > 0 && sh > 0) {
    return {
      width: `${(sw / fw) * 100}%`,
      height: `${(sh / fh) * 100}%`,
      maxWidth: 'none',
      left: `${-((Number(frame.x) || 0) / fw) * 100}%`,
      top: `${-((Number(frame.y) || 0) / fh) * 100}%`,
    }
  }
  return { width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }
}
function openDetails(item) {
  selected.value = item
  detailTab.value = 'info'
  walletPickerOpen.value = false
  bindingError.value = ''
  settingsOpen.value = false
  settingsError.value = ''
  abilityError.value = ''
  showAbilityHelp.value = false
}
function selectDetailTab(tab) {
  detailTab.value = tab
  abilityError.value = ''
  if (tab !== 'account') {
    walletPickerOpen.value = false
    settingsOpen.value = false
    return
  }
  walletPickerOpen.value = true
  if (selected.value && walletFor(selected.value)) {
    hydrateSettings(selected.value)
    settingsOpen.value = true
  }
}
function closeDetails() {
  selected.value = null
  detailTab.value = 'info'
  walletPickerOpen.value = false
  bindingError.value = ''
  settingsOpen.value = false
  settingsError.value = ''
  abilityError.value = ''
  showAbilityHelp.value = false
}

async function resetAbility(skill) {
  const item = selected.value
  const wallet = String(item?.wallet_public_key || '').trim()
  const trigger = String(skill?.trigger || (skill?.kind === 'survival' ? 'position_update' : 'feed_update'))
  if (!item?.owned || !wallet || !skill?.stats?.resettable || abilityBusy.value) return
  if (!window.confirm(`Reset ${skill.name} to the character's base program?`)) return
  abilityBusy.value = true
  abilityError.value = ''
  try {
    await api.resetCharacterAbility({
      plugin_id: item.plugin_id,
      character_id: item.character.id,
      wallet_public_key: wallet,
      trigger,
    })
    await loadCatalog()
    detailTab.value = 'info'
  } catch (cause) {
    abilityError.value = cause?.message || 'character_ability_reset_failed'
  } finally {
    abilityBusy.value = false
  }
}

async function primaryAction(item) {
  if (busyKey.value) return
  if (item.owned && !walletFor(item)) {
    selected.value = item
    detailTab.value = 'account'
    walletPickerOpen.value = true
    settingsOpen.value = false
    bindingError.value = ''
    return
  }
  if (item.owned && walletFor(item)) {
    busyKey.value = itemKey(item)
    error.value = ''
    try {
      let installationId = String(item.installation_id || installationFor(item)?.id || '').trim()
      if (isPersonaPlaying(item)) {
        if (installationId) await api.deactivateInstallation(installationId)
        await refreshInstallations()
        store.clearSelectedWalletKey()
        closeDetails()
        window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: 'console' } }))
        window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
        return
      }
      if (!installationId) {
        const installed = await api.installPlugin(item.plugin_id)
        installationId = String(installed?.installation?.id || '').trim()
      }
      if (!installationId) throw new Error('installation_missing')
      const current = installationFor(item)
      if (!current?.active) await api.activateInstallation(installationId)
      await refreshInstallations()
      selectPersona(item)
      closeDetails()
      window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: `plugin:${installationId}` } }))
      window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
    } catch (cause) {
      error.value = cause?.message || 'character_playback_failed'
    } finally {
      busyKey.value = ''
    }
    return
  }
  acquisitionItem.value = item
  acquisitionError.value = ''
  newWalletName.value = `${item.plugin_name || 'Game'} account`
  const selectedKey = String(selectedWallet.value?.public_key || '').trim()
  acquisitionWallet.value = availableWallets.value.some(wallet => wallet.public_key === selectedKey)
    ? selectedKey
    : (availableWallets.value[0]?.public_key || '')
}

async function toggleWalletBinding(wallet) {
  const item = selected.value
  const walletKey = String(wallet?.public_key || '').trim()
  if (!item?.owned || !walletKey || busyKey.value) return
  busyKey.value = itemKey(item)
  bindingError.value = ''
  try {
    let installationId = String(item.installation_id || '').trim()
    if (!installationId) {
      const installed = await api.installPlugin(item.plugin_id)
      installationId = String(installed?.installation?.id || '').trim()
    }
    if (!installationId) throw new Error('installation_missing')
    const current = String(item.wallet_public_key || '').trim()
    if (current === walletKey) {
      await api.unbindCharacter(installationId, walletKey)
    } else {
      await api.bindCharacter(installationId, walletKey, item.character.id)
      store.setSelectedWalletKey(walletKey)
    }
    await loadCatalog()
    walletPickerOpen.value = false
  } catch (cause) {
    bindingError.value = cause?.message || 'character_binding_failed'
  } finally {
    busyKey.value = ''
  }
}

function closeAcquisition(force = false) {
  if (busyKey.value && !force) return
  acquisitionItem.value = null
  acquisitionWallet.value = ''
  acquisitionError.value = ''
  newWalletName.value = ''
}

async function waitForCreatedWallet(existing, timeoutMs = 8000) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const wallet = wallets.value.find(row => !existing.has(row.public_key))
    if (wallet) return wallet
    await new Promise(resolve => window.setTimeout(resolve, 200))
  }
  throw new Error('wallet_create_sync_timeout')
}

async function confirmAcquisition() {
  const item = acquisitionItem.value
  if (!item || busyKey.value) return
  busyKey.value = itemKey(item)
  acquisitionError.value = ''
  try {
    let wallet = String(acquisitionWallet.value || '').trim()
    if (!availableWallets.value.length) {
      const existing = new Set(wallets.value.map(row => row.public_key))
      await store.createWallet({ name: String(newWalletName.value || '').trim() })
      const created = await waitForCreatedWallet(existing)
      wallet = created.public_key
      acquisitionWallet.value = wallet
    }
    if (!wallet) throw new Error('game_account_required')
    const payload = {
      plugin_id: item.plugin_id,
      version_id: item.version_id,
      character_id: item.character.id,
      wallet_public_key: wallet,
    }
    if (item.owned) {
      const installed = await api.installPlugin(item.plugin_id)
      const installationId = String(installed?.installation?.id || item.installation_id || '')
      if (!installationId) throw new Error('installation_missing')
      await api.bindCharacter(installationId, wallet, item.character.id)
    } else if (item.acquisition === 'free') {
      await api.acquireCharacter(payload)
    } else {
      const prepared = await api.prepareCharacterPurchase(payload)
      const purchase = prepared?.purchase || {}
      if (String(purchase.status || '') !== 'completed') {
        const seller = String(purchase.seller_public_key || '').trim()
        const lamports = Number(purchase.price_lamports || 0)
        if (!seller || !(lamports > 0)) throw new Error('purchase_quote_invalid')
        let signature = String(purchase.payment_signature || '').trim()
        if (!signature) {
          const payment = await walletsApi.transferWallet({ source: wallet, destination: seller, lamports })
          signature = String(payment?.signature || '').trim()
        }
        if (!signature) throw new Error('payment_signature_missing')
        const confirmed = await api.confirmCharacterPurchase(purchase.id, signature)
        if (confirmed?.pending) throw new Error('payment_confirmation_pending')
      }
    }
    await loadCatalog()
    store.setSelectedWalletKey(wallet)
    closeAcquisition(true)
  } catch (cause) {
    acquisitionError.value = cause?.message === 'payment_confirmation_pending'
      ? 'Payment is pending confirmation. Retry in a few seconds; the purchase is idempotent.'
      : (cause?.message || 'character_acquire_failed')
  } finally {
    busyKey.value = ''
  }
}
</script>

<style scoped>
.characters-directory{position:relative;width:100%;height:100%;min-height:0;padding:8px 8px calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom));background:#0a0a0a;overflow:hidden;color:#e5e7eb}
.panel{height:100%;min-height:0;display:flex;flex-direction:column;border:1px solid #222;border-radius:8px;background:#111;overflow:hidden}
.header{position:relative;min-height:48px;padding:0 10px 0 12px;display:flex;align-items:center;justify-content:space-between;gap:8px;border-bottom:1px solid #222;background:#1a1a1a}
.game-picker{position:relative;min-width:0}.game-title{max-width:min(68vw,420px);height:34px;display:flex;align-items:center;gap:7px;padding:0;border:0;background:transparent;color:#fff;font-size:14px;font-weight:850;cursor:pointer}.game-title span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.game-title i{width:7px;height:7px;border-right:1.5px solid #8fa1b5;border-bottom:1.5px solid #8fa1b5;transform:rotate(45deg) translateY(-2px)}
.game-menu{position:absolute;z-index:18;left:-5px;top:39px;min-width:210px;max-width:min(82vw,360px);max-height:45vh;overflow:auto;padding:5px;border:1px solid #2b3540;border-radius:9px;background:#090d12f5;box-shadow:0 14px 40px #000c;backdrop-filter:blur(12px)}.game-menu button{width:100%;min-height:34px;padding:7px 9px;border:0;border-radius:6px;background:transparent;color:#aab4c0;text-align:left;font-size:11px;font-weight:800}.game-menu button.active{background:#10213a;color:#dbeafe}.game-menu small{display:block;padding:9px;color:#718399;font-size:9px}
.header-tools{display:flex;align-items:center;gap:7px}.header-tools>small{color:#8fa1b5;font-size:9px;font-variant-numeric:tabular-nums}.filter-picker,.sort-picker{position:relative}.filter-menu{position:absolute;z-index:19;right:0;top:36px;min-width:126px;padding:5px;border:1px solid #2b3540;border-radius:9px;background:#090d12f5;box-shadow:0 14px 40px #000c;backdrop-filter:blur(12px)}.filter-menu button{width:100%;min-height:34px;padding:7px 9px;border:0;border-radius:6px;background:transparent;color:#aab4c0;text-align:left;font-size:11px;font-weight:800;cursor:pointer}.filter-menu button.active{background:#10213a;color:#dbeafe}.sort-menu{min-width:112px}
.body{flex:1;min-height:0;padding:8px;overflow-y:auto}.directory-state{min-height:240px;display:grid;place-items:center;align-content:center;gap:8px;text-align:center;color:#8fa1b5}.directory-state strong{color:#fff}.directory-state.error strong{color:#fca5a5}.directory-state button{height:30px;padding:0 10px;border:1px solid #2563eb;border-radius:8px;background:#10213a;color:#dbeafe}
.character-grid{list-style:none;padding:0;margin:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:8px}
.character-card{display:grid;grid-template-columns:118px minmax(0,1fr);min-height:178px;border:1px solid #222;border-radius:10px;background:#0f0f0f;overflow:hidden;cursor:pointer;outline:none}.character-card:hover,.character-card:focus-visible{border-color:#415166;background:#13171d}.character-card.playing{border-color:#245b3b;box-shadow:inset 0 0 0 1px #245b3b33}
.character-media{position:relative;min-height:100%;overflow:hidden;background:radial-gradient(circle at 50% 22%,#233348,#0b1115 72%)}.character-media img,.detail-hero img{position:absolute;left:0;top:0}
.character-main{min-width:0;padding:9px 10px;display:grid;align-content:start;gap:6px}.character-head{display:flex;align-items:flex-start;gap:8px}.character-head>div:first-child{min-width:0;display:grid;gap:1px}.character-flags{margin-left:auto;display:flex;align-items:center;gap:4px;flex:0 0 auto}.character-head small{color:#7dd3fc;font-size:8px;font-weight:900;text-transform:uppercase;letter-spacing:.04em}.character-head strong{color:#fff;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ownership{padding:3px 7px;border:1px solid #374151;border-radius:999px;color:#93c5fd;font-size:8px;font-weight:900;white-space:nowrap}.ownership.owned{border-color:#166534;background:#07170d;color:#86efac}
.character-main>p{margin:0;color:#7f8b9b;font-size:10px;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.ability-modified{display:inline-flex;align-items:center;padding:2px 6px;border:1px solid #a855f766;border-radius:999px;background:#6d28d922;color:#e9d5ff;font-size:8px;font-weight:900;letter-spacing:.04em;box-shadow:0 0 10px #a855f744}.ability-modified.detail{margin-left:2px}.ability-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.ability-row>span{min-width:0;padding:5px 6px;border:1px solid #222;border-radius:7px;background:#0b0d0f;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:1px 4px}.ability-row small{color:#8fa1b5;font-size:8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ability-row b{color:#c4b5fd;font-size:10px}.ability-row em{grid-column:1/-1;color:#596779;font-size:7px;font-style:normal}
.performance-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:5px}.performance-row>span{min-width:0;padding:5px 6px;border:1px solid #222;border-radius:7px;background:#0b0d0f;display:grid;gap:2px}.performance-row small{color:#718096;font-size:7px;font-weight:800;text-transform:uppercase;white-space:nowrap}.performance-row b{color:#dbeafe;font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.performance-row b.positive{color:#22c55e}.performance-row b.negative{color:#f87171}
.account-line{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:7px;align-items:center;padding:4px 6px;border:1px solid #203044;border-radius:6px;background:#0a1017;font-size:8px}.account-line strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#cbd5e1}.account-line.missing{border-style:dashed;color:#64748b}.account-line.missing strong{color:#94a3b8}.account-line strong.active{color:#86efac}.account-line>span{color:#e2e8f0}.positive{color:#22c55e!important}.negative{color:#f87171!important}.neutral{color:#94a3b8!important}
.card-footer,.tags{display:flex;align-items:center;gap:5px}.card-footer{margin-top:auto;min-width:0}.tags{min-width:0;overflow:hidden;flex:1}.tags span{color:#93c5fd;font-size:8px;white-space:nowrap}.nft-price{margin-left:auto;color:#f8d477;font-size:10px;letter-spacing:.02em;white-space:nowrap}
.empty{grid-column:1/-1;min-height:150px;display:grid;place-items:center;border:1px dashed #263039;border-radius:8px;text-align:center;color:#cbd5e1}
.detail-overlay{position:absolute;inset:0 0 calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom)) 0;z-index:20;display:grid;place-items:center;padding:12px;background:#000b;backdrop-filter:blur(8px)}
.detail-card{position:relative;width:min(620px,100%);height:min(720px,calc(100% - 4px));overflow:hidden;display:grid;grid-template-columns:230px minmax(0,1fr);grid-template-rows:auto minmax(0,1fr);gap:12px;padding:14px;border:1px solid #26313c;border-radius:12px;background:#0b0f12;color:#dbe5ef;box-shadow:0 20px 80px #000c}
.detail-close{position:absolute;z-index:3;right:9px;top:8px;width:30px;height:30px;display:grid;place-items:center;padding:0;border:1px solid #303b46;background:#071019dd;color:#cbd5e1;border-radius:999px;font-size:20px;line-height:1;cursor:pointer}
.detail-hero{position:relative;height:330px;overflow:hidden;display:grid;place-items:center;border-radius:10px;background:radial-gradient(circle at 50% 16%,#26384d,#090d11 72%)}.detail-frame{position:relative;display:block;width:100%;max-height:100%;overflow:hidden}
.detail-copy{padding:4px 34px 0 0}.detail-copy>small,.collection-copy>small,.detail-account small{color:#7dd3fc;font-size:8px;font-weight:900;letter-spacing:.05em}.detail-copy h2{margin:4px 0 6px;color:#fff;font-size:22px}.detail-copy p,.collection-copy p{margin:7px 0;color:#94a3b8;font-size:11px;line-height:1.5}.detail-badges{display:flex;gap:5px;flex-wrap:wrap}.detail-badges span{padding:2px 6px;border:1px solid #293542;border-radius:999px;color:#a9bad0;font-size:8px}.detail-commerce{display:grid;gap:8px;margin-top:7px}.detail-price{display:block;color:#f8d477;font-size:16px}.detail-quick-actions{display:flex;align-items:center;gap:6px;flex-wrap:wrap}.detail-pane{grid-column:1/-1;min-height:0;overflow:hidden;display:flex;flex-direction:column;gap:12px;padding-right:1px}.detail-pane.tab-info{overflow-y:auto}.detail-pane>.detail-account,.detail-pane>.detail-trades,.detail-pane>.detail-transfer{flex:1 1 auto;min-height:0}.detail-pane>.ability-details:last-child{flex:1 0 auto}.detail-quick-actions :deep(.icon-action-button:not(.active)){border-color:transparent;background:transparent;box-shadow:none;color:#94a3b8}.detail-quick-actions :deep(.icon-action-button:not(.active):hover){background:#111923;color:#dbeafe}.detail-trades,.detail-transfer{grid-column:1/-1;height:100%;min-height:0;overflow:hidden;border:1px solid #203044;border-radius:9px}.detail-transfer{border-color:#26313c}
.collection-copy,.detail-traits,.detail-abilities,.detail-account{grid-column:1/-1}.collection-copy{padding:9px 10px;border:1px solid #1f2933;border-radius:8px;background:#0b0d0f}.collection-copy strong{display:block;margin-top:2px;font-size:11px}
.detail-traits{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;align-items:stretch;min-width:0}
.detail-traits>div{min-width:0;overflow:hidden;padding:7px 6px;border:1px solid #1f2933;border-radius:7px;background:#0b0d0f;display:grid;align-content:center;gap:2px}
.detail-traits small{min-width:0;color:#64748b;font-size:8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.detail-traits strong{display:block;min-width:0;max-width:100%;font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

.detail-abilities{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:7px}.detail-abilities>div{padding:9px;border:1px solid #1f2933;border-radius:8px;background:#0b0d0f}.detail-abilities span{display:flex;justify-content:space-between;gap:8px}.detail-abilities strong{font-size:11px}.detail-abilities b{color:#c4b5fd}.detail-abilities p{margin:4px 0;color:#8190a0;font-size:9px;line-height:1.35}.detail-abilities small{color:#64748b;font-size:8px}
.detail-account{padding:10px;border:1px solid #203044;border-radius:9px;background:#0a1017;overflow-y:auto}.account-summary{width:100%;display:flex;justify-content:space-between;gap:10px;align-items:center;padding:0;border:0;background:transparent;color:inherit;text-align:left;cursor:pointer}.account-summary.open{color:#dbeafe}.account-summary:disabled{opacity:.55;cursor:default}.account-summary>div:first-child{min-width:0;display:grid;gap:1px}.account-summary strong{font-size:12px}.account-summary span{color:#8fa1b5;font-size:9px}.account-money{margin-left:auto;display:grid;text-align:right;gap:1px}.account-money strong{font-size:12px}.account-money span{font-size:9px}.account-chevron{font-size:20px!important;line-height:1;transform:rotate(90deg);transition:transform .15s}.account-summary.open .account-chevron{transform:rotate(-90deg)}
.wallet-assignment{display:grid;gap:5px;margin-top:9px;padding-top:9px;border-top:1px solid #203044}.wallet-assignment>button{width:100%;display:grid;grid-template-columns:18px minmax(0,1fr) auto;align-items:center;gap:7px;padding:7px 8px;border:1px solid #293542;border-radius:7px;background:#080c10;color:#cbd5e1;text-align:left;cursor:pointer}.wallet-assignment>button.selected{border-color:#2563eb;background:#0d1b30}.wallet-assignment>button:disabled{opacity:.55;cursor:default}.wallet-choice-mark{position:relative;width:15px;height:15px;border:1px solid #45566a;border-radius:5px;background:#0a1119;box-shadow:inset 0 1px 2px #0008}.wallet-choice-mark.selected{border-color:#3b82f6;background:linear-gradient(180deg,#2563eb,#174da8);box-shadow:0 0 0 2px #2563eb22}.wallet-choice-mark.selected::after{content:"";position:absolute;left:4px;top:1px;width:4px;height:8px;border-right:2px solid #eff6ff;border-bottom:2px solid #eff6ff;transform:rotate(45deg)}.wallet-assignment button>span{display:grid;gap:1px;min-width:0}.wallet-assignment small{color:#718399!important;font-size:8px!important}.wallet-assignment em{color:#a9bad0;font-size:9px;font-style:normal}.binding-empty{margin:0;color:#718399;font-size:9px}
.settings-actions button{height:27px;padding:0 9px;border:1px solid #2b3a4a;border-radius:7px;background:#0d1620;color:#cbd5e1;font-size:9px;font-weight:800}
.account-settings{margin-top:9px;padding-top:9px;border-top:1px solid #203044;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px}.account-settings label{min-width:0;display:grid;gap:4px}.account-settings label>span{color:#64748b;font-size:8px}.account-settings input{width:100%;height:28px;box-sizing:border-box;border:1px solid #293542;border-radius:6px;background:#080c10;color:#e5e7eb;padding:0 7px;font-size:10px}.name-field{grid-column:span 2}.toggle-field{align-content:start}.toggle{height:28px;border:1px solid #3b4651;border-radius:6px;background:#0d1217;color:#94a3b8;font-size:8px;font-weight:900}.toggle.on{border-color:#166534;background:#07170d;color:#86efac}.settings-error{grid-column:1/-1;margin:0;color:#fca5a5;font-size:9px}.settings-actions{grid-column:1/-1;display:flex;justify-content:flex-end;gap:6px}.settings-actions .save{border-color:#2563eb;background:#10213a;color:#dbeafe}
.acquisition-overlay{position:absolute;inset:0;z-index:30;display:grid;place-items:center;padding:12px;background:#000c;backdrop-filter:blur(8px)}
.acquisition-card{width:min(520px,100%);max-height:calc(100% - 8px);overflow:auto;padding:14px;border:1px solid #2d3a47;border-radius:12px;background:#0b0f12;color:#dbe5ef;box-shadow:0 24px 90px #000d}.acquisition-card>header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.acquisition-card h2{margin:3px 0 0;color:#fff;font-size:18px}.acquisition-card header small{color:#7dd3fc;font-size:8px;font-weight:900;text-transform:uppercase}.acquisition-card header button{width:30px;height:30px;border:1px solid #303b46;border-radius:999px;background:#071019;color:#cbd5e1;font-size:20px}.acquisition-copy,.new-wallet-choice p{margin:10px 0;color:#94a3b8;font-size:11px;line-height:1.5}
.wallet-choice{margin:12px 0;padding:0;border:0;display:grid;gap:6px}.wallet-choice legend{margin-bottom:7px;color:#cbd5e1;font-size:10px;font-weight:900}.wallet-choice label{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:9px;padding:9px;border:1px solid #293542;border-radius:8px;background:#0a1017;cursor:pointer}.wallet-choice label.selected{border-color:#2563eb;background:#10213a}.wallet-choice label span{min-width:0;display:grid;gap:1px}.wallet-choice label strong{font-size:11px}.wallet-choice label small,.wallet-choice label em{color:#8fa1b5;font-size:9px;font-style:normal}
.new-wallet-choice{margin:12px 0;padding:11px;border:1px solid #293542;border-radius:8px;background:#0a1017}.new-wallet-choice>strong{font-size:12px;color:#fff}.new-wallet-choice label{display:grid;gap:4px}.new-wallet-choice label span{color:#8fa1b5;font-size:9px}.new-wallet-choice input{height:32px;border:1px solid #293542;border-radius:7px;background:#080c10;color:#e5e7eb;padding:0 9px}
.acquisition-summary{display:grid;grid-template-columns:1fr auto;gap:2px 8px;padding:9px 10px;border:1px solid #2b3139;border-radius:8px;background:#101214}.acquisition-summary span{font-size:10px;font-weight:900}.acquisition-summary strong{color:#f8d477;font-size:11px}.acquisition-summary small{grid-column:1/-1;color:#8fa1b5;font-size:9px}.acquisition-error{margin:9px 0 0;color:#fca5a5;font-size:10px}.acquisition-card>footer{display:flex;justify-content:flex-end;gap:7px;margin-top:12px}.acquisition-card>footer button{height:31px;padding:0 12px;border:1px solid #303b46;border-radius:7px;background:#0d1620;color:#cbd5e1;font-size:10px;font-weight:800}.acquisition-card>footer .confirm{border-color:#2563eb;background:#10213a;color:#dbeafe}.acquisition-card button:disabled{opacity:.45}
@media(max-width:760px){.persona-hint{display:none}.character-grid{grid-template-columns:1fr}.detail-overlay{padding:6px}.detail-card{max-height:100%;grid-template-columns:124px minmax(0,1fr);padding:10px;gap:9px}.detail-hero{height:190px}.detail-copy h2{font-size:18px}.collection-copy,.detail-traits,.detail-account{grid-template-columns:1fr}.account-settings{grid-template-columns:repeat(3,minmax(0,1fr))}.name-field{grid-column:span 2}.detail-primary{justify-self:stretch}.game-tabs{padding-top:4px;padding-bottom:4px}}
@media(max-width:420px){.character-card{grid-template-columns:106px minmax(0,1fr);min-height:166px}.character-main{padding:8px}.ability-row>span{padding:4px 5px}.account-settings{grid-template-columns:repeat(2,minmax(0,1fr))}.detail-card{grid-template-columns:110px minmax(0,1fr)}.detail-hero{height:174px}}
.ability-details{grid-column:1/-1;display:grid;gap:7px;padding:10px;border:1px solid #25313d;border-radius:9px;background:#0b0f12}.ability-details.modified{border-color:#3b2b55;background:#120d1a}.ability-details>header{display:flex;align-items:center;justify-content:space-between;gap:8px}.ability-details>header small,.ability-details .patch-copy>small{color:#8fa1b5;font-size:8px;font-weight:900;letter-spacing:.05em}.ability-details.modified>header small,.ability-details.modified .patch-copy>small{color:#c4b5fd}.ability-details>header strong{display:block;margin-top:2px;font-size:12px}.ability-details>div{padding:9px;border:1px solid #31283d;border-radius:8px;background:#0c0910}.ability-details>div>header{display:flex;align-items:center;justify-content:space-between;gap:8px}.ability-details>div>header>span{display:grid;gap:1px}.ability-details>div>header small{color:#7f7190;font-size:8px}.ability-details p{margin:6px 0 0;color:#a9a0b5;font-size:10px;line-height:1.5}.patch-copy{margin-top:8px;padding:7px;border-left:2px solid #a855f7;background:#6d28d914}.patch-copy p{color:#e9d5ff}
.ability-wand{width:36px;height:36px;padding:0;border:1px solid #8b5cf6;border-radius:9px;background:linear-gradient(180deg,#3b1f67,#21123b);font-size:17px;box-shadow:0 0 16px #8b5cf644}.ability-wand.inline{flex:0 0 auto}.ability-help{display:grid;gap:10px}.ability-help p{margin:0;color:#aab4c0;font-size:11px;line-height:1.55}.ability-prompt{display:grid;gap:4px;padding:10px;border:1px solid #5b3b82;border-radius:8px;background:#24143b}.ability-prompt small{color:#c4b5fd;font-size:8px;font-weight:900}.ability-prompt strong{color:#f3e8ff;font-size:11px;line-height:1.45}
</style>
